import React, { useEffect, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Typography from "@mui/material/Typography";

import useAccountSettings from "@app/analytics/hooks/settings/useAccountSettings";
import useWorkspaceInvite from "@app/analytics/hooks/workspace/useWorkspaceInvite";
import NextButton from "@app/analytics/onboarding/components/NextButton";
import { TextButton } from "@app/analytics/onboarding/components/styles";
import useOnboardingStore from "@app/analytics/onboarding/hooks/useOnboardingStore";
import useAuth from "@app/common/auth/useAuth";
import Link from "@app/common/components/Link";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { usePageTitle } from "@app/utils/hooks";

import InfoPanel from "@rui/InfoPanel";
import Stack, { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  StepCardContent,
  StepDescription,
  StepFooter,
  StepHeader,
  StepTitle,
} from "./styles";

const WorkspaceCard = () => {
  usePageTitle("Welcome - Workspace");
  const { user } = useAuth();

  const { goToNextStep } = useOnboardingStore();

  const { resendEmailVerification } = useAccountSettings();
  const { invite, updateInvite, isLoading } = useWorkspaceInvite();
  const [loadingAction, setLoadingAction] = useState<
    "accept" | "reject" | null
  >(null);

  const handleJoin = (inviteId: string, isAccepted: boolean) => {
    setLoadingAction(isAccepted ? "accept" : "reject");
    updateInvite(
      { inviteId, isAccepted },
      {
        onError: (error) => {
          if (isResponseErrorWithStatus(error, 402)) {
            toast.error(
              "Sorry! This Workspace is currently full. Please reach out to the Workspace owner to add additional seats."
            );
          } else {
            toast.error("Unable to join Workspace. Please try again later.");
          }
        },
        onSettled: () => setLoadingAction(null),
      }
    );
  };

  const hasNoInvite = !isLoading && invite == null;

  // fallback if invite is not available
  useEffect(() => {
    if (hasNoInvite) {
      goToNextStep("skipped");
    }
  }, [hasNoInvite]);

  if (!invite) return null;

  return (
    <>
      <StepCardContent spacing={3}>
        <StepHeader>
          <StepTitle>Join Workspace</StepTitle>
          <StepDescription>
            Workspaces help teams build holistic views of meeting metrics, and
            allow for custom permissions across groups.{" "}
            <Link href={LINKS.MARKETING.WORKSPACES} sx={{ display: "inline" }}>
              Learn More
            </Link>
            .
          </StepDescription>
        </StepHeader>
        {invite.status === "pending" && !user?.isEmailVerified && (
          <InfoPanel
            severity="warning"
            action={{
              label: "Resend Verification",
              onClick: () => resendEmailVerification.mutate(),
            }}
          >
            You must verify your email to join this Workspace. Please click the
            link in your inbox to verify your email.
          </InfoPanel>
        )}
        <Row
          variant="apart"
          sx={{
            width: "100%",
            padding: "16px 24px",
            border: (theme) => `1px solid ${theme.palette.shades.neutral[100]}`,
            borderRadius: "16px",
          }}
          spacing={3}
        >
          {invite.status === "accepted" && (
            <StepDescription>
              🎉 Woohoo! You’re now part of the {invite.workspaceName}{" "}
              Workspace. Continue to complete account setup.
            </StepDescription>
          )}
          {invite.status === "denied" && (
            <StepDescription>
              You declined the invite to join the {invite.workspaceName}{" "}
              Workspace, but you can still use Read for better meeting outcomes.
              Continue to complete account setup.
            </StepDescription>
          )}
          {invite.status === "pending" && (
            <Stack
              direction={{ xxs: "column", sm: "row" }}
              spacing={2}
              variant="apart"
              width="100%"
            >
              <Stack spacing="4px">
                <Typography variant="h3">{invite.workspaceName}</Typography>
                <StepDescription sx={{ textAlign: "left" }}>
                  {invite.inviterFirstName} {invite.inviterLastName} (
                  {invite.inviterEmail}) invited you to the{" "}
                  {invite.workspaceName} Workspace.
                </StepDescription>
              </Stack>
              <Row spacing={2}>
                <LoadingButton
                  variant="text"
                  sx={{ height: "44px" }}
                  onClick={() => handleJoin(invite.id, false)}
                  loading={loadingAction === "reject"}
                >
                  Decline
                </LoadingButton>
                <TooltipWrapper
                  title="You must first verify your email to join this Workspace."
                  show={!user?.isEmailVerified}
                >
                  <LoadingButton
                    variant="outlined"
                    sx={{ height: "44px" }}
                    onClick={() => handleJoin(invite.id, true)}
                    loading={loadingAction === "accept"}
                    disabled={!user?.isEmailVerified}
                  >
                    Accept
                  </LoadingButton>
                </TooltipWrapper>
              </Row>
            </Stack>
          )}
        </Row>
      </StepCardContent>
      <StepFooter>
        {invite.status === "pending" && (
          <TextButton onClick={() => goToNextStep("skipped")}>
            Skip this step
          </TextButton>
        )}
        <NextButton
          onClick={() =>
            goToNextStep(invite.status === "accepted" ? "complete" : "skipped")
          }
          disabled={invite.status === "pending"}
        />
      </StepFooter>
    </>
  );
};

export default WorkspaceCard;
